import _ from "lodash";
import { Input, message } from "antd";
import { useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";

import { useAuthStore } from "../../../store";
import { UserException } from "../../../utils/helpers";
import { parseError } from "../../../utils/parseError";
import { OTPView } from "../../../components/secondary";
import AuthForm from "../../../components/forms/AuthForm";
import BlueButton from "../../../components/buttons/BlueButton";
import { CelluarInput, CheckBox } from "../../../components/inputs";
import { BodyOne, HeadingOne } from "../../../components/typography";
import { CONFIRM_SIGN_UP, SIGN_UP } from "../../../graphql/Mutations";
import InputValidator from "../../../components/inputs/InputValidator";
import { validators } from "../../../constants";
import CheckBoxWithText from "../../../components/secondary/CheckBoxWithText";
import { useLoginMutation, useRespondAuthChallegeVariables, useRespondAuthChallenge, useVerificationOTP } from "../../../hooks";
import SupportBlock from "../../../components/info/SupportBlock";
import mixpanelService from "../../../services/mixpanel";
import bugfenderHelper from "../../../services/bugfender";


/**
 * First signup step: account fields, email OTP, SMS MFA, then company setup.
 * @param {{ nextStep: Function, formChangeHandler: Function }} props
 */
const AccountDetailsForm = ({ nextStep, formChangeHandler }) => {
  const navigate = useNavigate();
  const { handleSubmit, formState: { errors }, control, getValues } = useForm();
  const [accountCreationLevel, setAccountCreationLevel] = useState(1);
  const signUpFormInputs = useAuthStore((s) => s.signUpInputs);
  const setSignUpFormInputs = useAuthStore((s) => s.setSignUpInputs);

  const [signUpMutation, {
    loading: signUpLoading,
    data: signUpData,
    error: signUpError
  }] = useMutation(SIGN_UP);

  const [confirmSignUpMutation, {
    loading: confirmSignUpLoading,
    data: confirmSignUpData,
    error: confirmSignUpError
  }] = useMutation(CONFIRM_SIGN_UP);

  const { OTP, handleSetOTP } = useVerificationOTP();
  const { OTP: confirmSignUpOTP, handleSetOTP: handleConfirmSignUpSetOTP } = useVerificationOTP();
  const { verificationCodeDestination, session, username } = useRespondAuthChallegeVariables();

  const { handleOTP, respondeAuthLoading } = useRespondAuthChallenge({
    isAfterSignUp: true,
    variables: {
      smsMfaCode: OTP,
      username,
      session,
      email: signUpFormInputs.email.toLowerCase(),
      password: signUpFormInputs.password
    }
  });

  const { loading: loginLoading, handleLogin } = useLoginMutation({
    isAfterSignUp: true,
    inviteDetail: undefined,
    variable: {
      username: signUpFormInputs.email.toLowerCase(),
      password: signUpFormInputs.password
    }, onCompletedCallback: () => {
      setAccountCreationLevel(3);
    },nextStep
  });

  const genders = [
    { value: "Male", label: "Male", id: 1 },
    { value: "Female", label: "Female", id: 2 },
    { value: "None", label: "Prefer not to say", id: 3 },
  ];

  const onSubmit = async (data) => {
    try {
      const input = {
        email: data.email.toLowerCase(),
        gender: data.gender,
        firstName: data.name,
        lastName: data.family_name,
        password: data.password,
        phoneNumber: `${data.phone_number}`
      }

      signUpMutation({
        variables: { input }
      });
    } catch (err) {
      console.error("AccountDetailsForm | onSubmit [ERROR]", err);
    }
  };

  const handleConfirmSignUpOTP = useCallback(() => {
    const formEmail = signUpFormInputs?.email.toLowerCase();

    if (!formEmail) {
      const errorMessage = "handleConfirmSignUpOTP - Form Email is undefined";
      bugfenderHelper.error(errorMessage);
      throw UserException(errorMessage);
    }

    confirmSignUpMutation({
      variables: {
        code: confirmSignUpOTP,
        email: formEmail
      }
    });
  }, [confirmSignUpOTP, signUpFormInputs?.email]);


  const handleSetUpCompany = async () => {
    const formData = {
      email: signUpFormInputs.email.toLowerCase(),
      gender: signUpFormInputs.gender,
      firstName: signUpFormInputs.firstName,
      lastName: signUpFormInputs.lastName,
      password: signUpFormInputs.password,
      phoneNumber: signUpFormInputs.phoneNumber
    }
    await formChangeHandler(formData, 'account');
    nextStep();
  }

  useEffect(() => {
    if (!signUpData) return;

    if (signUpData?.signUp?.Error) {
      message.error(signUpData.signUp.Error.message);
      return;
    }

    const processSignUp = async () => {
      try {
        const formValues = getValues();

        const signUpInput = {
          email: formValues.email.toLowerCase(),
          gender: formValues.gender,
          firstName: formValues.name,
          lastName: formValues.family_name,
          password: formValues.password,
          phoneNumber: formValues.phone_number
        }

        if (signUpData?.signUp) {
          message.success(signUpData.signUp.text);

          setSignUpFormInputs({
            ...signUpInput,
            username: signUpData.signUp.username
          });

          setAccountCreationLevel(2);
          return;
        }

        throw null;
      } catch (err) {
        bugfenderHelper.error(err);
        console.error("signUpMutation [ERROR]\n", err);
        message.error("We've encountered an issue with your registration.");
      }
    }

    processSignUp();
  }, [signUpData]);

  useEffect(() => {
    if (!signUpError) return;

    const processSignUpError = async () => {
      try {
        const errorMessage = parseError(signUpError);
        message.error(errorMessage);
      } catch (err) {
        message.error("We've encountered an issue with your registration.");
        console.error(UserException("signUpMutation - Failed to register user"));
      }
    }

    processSignUpError();
  }, [signUpError]);

  useEffect(() => {
    if (!confirmSignUpData) return;

    const processConfirmSignUp = async () => {
      try {
        if (confirmSignUpData?.confirmSignUp !== null) {
          message.success("Verification successful")
          handleLogin();
          return;
        }

        throw null;
      } catch (err) {
        console.error("confirmSignUpMutation [ERROR]\n", err);
        message.error("We've encountered an issue with your registration.");
      }
    }

    processConfirmSignUp();
  }, [confirmSignUpData]);

  useEffect(() => {
    if (!confirmSignUpError) return;

    const processConfirmSignUpError = async () => {
      const errorMessage = "We've encountered an issue with your registration confirmation.";

      try {
        bugfenderHelper.error(confirmSignUpError);
        message.error(errorMessage);
      } catch (err) {
        message.error(errorMessage);
        console.error("confirmSignUpMutation - Failed to confirm user sign-up");

        mixpanelService.track("App.Register.Confirm.Error", {error: confirmSignUpError});
      }
    }

    processConfirmSignUpError();
  }, [confirmSignUpError]);

  if (accountCreationLevel === 2) {
    return (
      <AuthForm>
        <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
          Verification step
        </HeadingOne>
        <OTPView
          isSignUpOTP={true}
          disabled={confirmSignUpLoading}
          OTP={confirmSignUpOTP}
          verificationCodeDestination={signUpFormInputs.email}
          handleSetOTP={handleConfirmSignUpSetOTP}
          handleOTP={handleConfirmSignUpOTP}
        />
        <BlueButton
          className="w-full mt-4 py-2.5 font-semibold hover:text-white"
          onClick={handleConfirmSignUpOTP}
          isLoading={confirmSignUpLoading}
          disabled={confirmSignUpLoading || confirmSignUpOTP.length !== 6}
        >
          Continue
        </BlueButton>
        <div className="auth-footer-rule" />
        <SupportBlock area="sign-up-index-step-2" tailwindProps="mt-0" />
      </AuthForm>
    )
  }

  if (accountCreationLevel === 3) {
    return (
      <AuthForm>
        <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
          Verification step
        </HeadingOne>
        <OTPView
          OTP={OTP}
          disabled={respondeAuthLoading || loginLoading}
          verificationCodeDestination={verificationCodeDestination}
          handleSetOTP={handleSetOTP}
          handleOTP={handleOTP}
        />
        <BlueButton
          className="w-full mt-4 py-2.5 font-semibold hover:text-white"
          onClick={handleOTP}
          disabled={respondeAuthLoading || loginLoading || OTP.length !== 6}
          isLoading={respondeAuthLoading || loginLoading}
        >
          Continue
        </BlueButton>
        <div className="auth-footer-rule" />
        <SupportBlock area="sign-up-index-step-3" tailwindProps="mt-0" />
      </AuthForm>
    )
  }

  if (accountCreationLevel === 4) {
    return (
      <AuthForm>
        <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
          Thanks for verifying
        </HeadingOne>
        <BodyOne className="text-center pb-2 font-semibold" style={{ color: "#003EB2" }} >
          Your account is now secured with Multi Factor Authentication!
        </BodyOne>
        <CheckBoxWithText textColor="#006924" iconColor="#006924" className="mt-3" />
        <CheckBoxWithText textColor="#006924" iconColor="#006924" className="mt-4" />
        <BlueButton
          className="w-full mt-6 py-2.5 font-semibold hover:text-white"
          type="button"
          onClick={handleSetUpCompany}
        >
          Setup Company
        </BlueButton>
        <div className="auth-footer-rule" />
        <SupportBlock area="sign-up-index-step-4" tailwindProps="mt-0" />
      </AuthForm>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AuthForm>
        <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
          Create Account
        </HeadingOne>
        <div className="flex flex-col">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Name</BodyOne>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Input size="large" placeholder="Enter your first name here" onChange={onChange} value={value} />
            )}
          />
          {errors.name && (
            <BodyOne className="text-red-600">
              Please enter your name
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Last Name</BodyOne>
          <Controller
            name="family_name"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Input size="large" placeholder="Enter your last name here" onChange={onChange} value={value} />
            )}
          />
          {errors.family_name && (
            <BodyOne className="text-red-600">
              Please enter your last name
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Email address</BodyOne>
          <Controller
            name="email"
            control={control}
            defaultValue={""}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Input size="large" placeholder="Enter your email address here" onChange={onChange} value={value} disabled={false} />
            )}
          />
          {errors.email && (
            <BodyOne className="text-red-600">
              Please enter your email
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Password</BodyOne>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{ required: true, validate: (value) => validators.isPasswordValid(value) }}
            render={({ field: { onChange, value } }) => (
              <InputValidator
                validatorStates={validators.getPasswordValidatorStates(value)}
                iconTitles={["8+ Letters", "1 Uppercase", "1 Number"]}>
                <Input.Password style={{ border: "1px solid transparent" }} size="large" placeholder="Enter your password here" onChange={onChange} value={value} />
              </InputValidator>
            )}
          />
          {errors.password && (
            <BodyOne className="text-red-600">
              {errors.password.message}
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Mobile number</BodyOne>
          <Controller
            name="phone_number"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <CelluarInput
                onChange={onChange}
                value={value}
              />
            )}
          />
          {errors.phone_number && (
            <BodyOne className="text-red-600">
              Please enter your mobile number
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium">Gender</BodyOne>
          <div className="flex justify-between flex-wrap gap-2">
            <Controller
              name="gender"
              control={control}
              defaultValue=""
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <>
                  {genders.map((gender) => (
                    <div key={gender.id} className="flex flex-row">
                      <CheckBox
                        className="mt-1 mr-2"
                        name={gender.value}
                        onChange={() => onChange(gender.value)}
                        value={value}
                      />
                      <span>{gender.label}</span>
                    </div>
                  ))}
                </>
              )}
            />
          </div>
        </div>
        <BlueButton
          className="w-full mt-6 py-2.5 font-semibold hover:text-white"
          type="submit"
          isLoading={signUpLoading}
        >
          Proceed
        </BlueButton>
        <p className="text-center mt-5 mb-0 text-xs leading-5 text-slate-500 font-poppins">
          By continuing you agree to our{' '}
          <Link className="text-[#0458f6] font-medium hover:underline" to="/legal/terms-of-use">Terms of Service</Link>
          {' '}and{' '}
          <Link className="text-[#0458f6] font-medium hover:underline" to="/legal/privacy-policy">Privacy Policy</Link>
        </p>
        <p className="text-center mt-3 mb-0 text-xs leading-5 text-slate-500 font-poppins">
          Already have an account?{" "}
          <span
            className="text-[#0458f6] font-medium cursor-pointer hover:underline"
            onClick={() => navigate("/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM=")}
          >
            Login
          </span>
        </p>
        <div className="auth-footer-rule" />
        <SupportBlock area="sign-up-index-step-1" tailwindProps="mt-0" />
      </AuthForm>
    </form>
  );
};

export default AccountDetailsForm;
